import { supabase } from './supabase'
import {
  createMedicalDocument,
  deleteMedicalDocument,
  getMedicalDocuments,
  updateMedicalDocument
} from './medical-documents'
import type {
  DocumentType,
  ExpensesBySpecialty,
  MedicalDocument,
  MedicalInvoice,
  MedicalInvoiceInput,
  MedicalSpecialty,
  MonthlyExpenses
} from '@/types/health'

export type InvoiceStorageMode = 'table' | 'document-fallback'

const FALLBACK_DOCUMENT_TYPE: DocumentType = 'insurance_document'
const FALLBACK_RECORD_TYPE = 'invoice_fallback'
const FALLBACK_TAG = 'invoice-fallback'
const DEFAULT_LIMIT = 50

const VALID_SPECIALTIES: MedicalSpecialty[] = [
  'general_medicine',
  'gastroenterology',
  'gynecology',
  'laboratory',
  'radiology',
  'pharmacy',
  'physiotherapy',
  'psychology',
  'dermatology',
  'cardiology',
  'endocrinology',
  'rheumatology',
  'neurology',
  'surgery',
  'emergency',
  'other'
]

let invoiceStorageModePromise: Promise<InvoiceStorageMode> | null = null

function todayDate() {
  return new Date().toISOString().split('T')[0]
}

function normalizeText(value: string | null | undefined): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function toStringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

function toMoney(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.round(value * 100) / 100
  }

  if (typeof value === 'string' && value.trim()) {
    const normalized = value
      .replace(/[€\s\u00a0]/g, '')
      .replace(/\((.+)\)/, '-$1')
      .replace(',', '.')
    const parsed = Number.parseFloat(normalized)
    if (Number.isFinite(parsed)) {
      return Math.round(parsed * 100) / 100
    }
  }

  return fallback
}

function toBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const normalized = normalizeText(value)
    if (['true', '1', 'oui', 'paye', 'payee', 'regle', 'reglee', 'paid'].includes(normalized)) return true
    if (['false', '0', 'non', 'impaye', 'impayee'].includes(normalized)) return false
  }
  return fallback
}

function normalizeSpecialty(value: unknown): MedicalSpecialty {
  return VALID_SPECIALTIES.includes(value as MedicalSpecialty) ? (value as MedicalSpecialty) : 'other'
}

function isMissingRelationError(error: unknown, relationNames: string[]): boolean {
  if (!error || typeof error !== 'object') return false
  const code = String((error as { code?: unknown }).code || '')
  const message = String((error as { message?: unknown }).message || '')
  if (code !== 'PGRST205' && code !== '42P01') return false
  return relationNames.some((relationName) => message.includes(relationName))
}

function setInvoiceStorageMode(mode: InvoiceStorageMode) {
  invoiceStorageModePromise = Promise.resolve(mode)
}

function getSafeMetadata(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }

  return {}
}

function buildRemainingCharge(input: Partial<MedicalInvoiceInput>): number {
  const total = toMoney(input.total_amount, 0)
  const secu = toMoney(input.secu_reimbursed, 0)
  const mutuelle = toMoney(input.mutuelle_reimbursed, 0)

  return Math.max(total - secu - mutuelle, 0)
}

function buildOutstandingProviderBalance(input: Partial<MedicalInvoiceInput>): number {
  const total = toMoney(input.total_amount, 0)
  const patientPaid = toMoney(input.patient_paid_amount, 0)

  return Math.max(total - patientPaid, 0)
}

export function getInvoiceReimbursedAmount(invoice: Partial<MedicalInvoiceInput>): number {
  return toMoney(invoice.secu_reimbursed, 0) + toMoney(invoice.mutuelle_reimbursed, 0)
}

export function getInvoiceRemainingPatientCharge(invoice: Partial<MedicalInvoiceInput>): number {
  return buildRemainingCharge(invoice)
}

export function getInvoiceOutstandingAmount(invoice: Partial<MedicalInvoiceInput>): number {
  return buildOutstandingProviderBalance(invoice)
}

function sanitizeInvoiceInput(input: MedicalInvoiceInput): MedicalInvoiceInput {
  const serviceDate = toStringValue(input.service_date) || todayDate()
  const invoiceDate = toStringValue(input.invoice_date) || serviceDate

  return {
    patient_id: input.patient_id,
    invoice_number: toStringValue(input.invoice_number),
    practitioner_name: toStringValue(input.practitioner_name) || 'Praticien non identifié',
    medical_facility: toStringValue(input.medical_facility),
    specialty: normalizeSpecialty(input.specialty),
    service_date: serviceDate,
    invoice_date: invoiceDate,
    total_amount: toMoney(input.total_amount, 0),
    patient_paid_amount: toMoney(input.patient_paid_amount, 0),
    is_paid: toBoolean(input.is_paid, false),
    payment_date: toStringValue(input.payment_date),
    payment_method: toStringValue(input.payment_method),
    secu_base: input.secu_base == null ? null : toMoney(input.secu_base, 0),
    secu_rate: input.secu_rate == null ? null : toMoney(input.secu_rate, 0),
    secu_reimbursed: input.secu_reimbursed == null ? null : toMoney(input.secu_reimbursed, 0),
    mutuelle_reimbursed: input.mutuelle_reimbursed == null ? null : toMoney(input.mutuelle_reimbursed, 0),
    document_url: toStringValue(input.document_url),
    notes: toStringValue(input.notes),
    metadata: getSafeMetadata(input.metadata)
  }
}

function sanitizeFallbackFileName(value: string): string {
  return (
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 80) || 'invoice'
  )
}

function buildFallbackDocument(input: MedicalInvoiceInput) {
  const normalized = sanitizeInvoiceInput(input)
  const extractedData = {
    ...normalized.metadata,
    system_record_type: FALLBACK_RECORD_TYPE,
    invoice_number: normalized.invoice_number,
    service_date: normalized.service_date,
    invoice_date: normalized.invoice_date,
    total_amount: normalized.total_amount,
    patient_paid_amount: normalized.patient_paid_amount ?? 0,
    is_paid: normalized.is_paid ?? false,
    payment_date: normalized.payment_date ?? null,
    payment_method: normalized.payment_method ?? null,
    secu_base: normalized.secu_base ?? null,
    secu_rate: normalized.secu_rate ?? null,
    secu_reimbursed: normalized.secu_reimbursed ?? 0,
    mutuelle_reimbursed: normalized.mutuelle_reimbursed ?? 0,
    remaining_patient_charge: buildRemainingCharge(normalized),
    provider_outstanding_amount: buildOutstandingProviderBalance(normalized),
    document_url: normalized.document_url ?? null
  }

  return {
    patient_id: normalized.patient_id,
    document_type: FALLBACK_DOCUMENT_TYPE,
    title: normalized.practitioner_name ? `Facture ${normalized.practitioner_name}` : 'Facture médicale',
    description: normalized.notes ?? 'Enregistrement facture',
    file_name: `invoice-${normalized.service_date}-${sanitizeFallbackFileName(normalized.practitioner_name)}.json`,
    file_url: normalized.document_url ?? null,
    file_size_bytes: null,
    mime_type: 'application/json',
    document_date: normalized.service_date,
    practitioner_name: normalized.practitioner_name,
    medical_facility: normalized.medical_facility ?? null,
    specialty: normalized.specialty,
    extracted_data: extractedData,
    tags: [FALLBACK_TAG],
    is_urgent: false,
    is_confidential: true,
    shared_with_doctors: []
  }
}

function isFallbackInvoiceDocument(document: MedicalDocument): boolean {
  const extractedData = getSafeMetadata(document.extracted_data)

  return (
    extractedData.system_record_type === FALLBACK_RECORD_TYPE ||
    document.tags?.includes(FALLBACK_TAG) === true
  )
}

function mapFallbackDocumentToInvoice(document: MedicalDocument): MedicalInvoice {
  const extractedData = getSafeMetadata(document.extracted_data)
  const totalAmount = toMoney(extractedData.total_amount, 0)
  const secuReimbursed = toMoney(extractedData.secu_reimbursed, 0)
  const mutuelleReimbursed = toMoney(extractedData.mutuelle_reimbursed, 0)
  const patientPaidAmount = toMoney(extractedData.patient_paid_amount, 0)
  const correctedRemainingCharge = buildRemainingCharge({
    total_amount: totalAmount,
    secu_reimbursed: secuReimbursed,
    mutuelle_reimbursed: mutuelleReimbursed
  })

  return {
    id: document.id,
    patient_id: document.patient_id,
    invoice_number: toStringValue(extractedData.invoice_number),
    practitioner_name: document.practitioner_name || 'Praticien non identifié',
    medical_facility: document.medical_facility,
    specialty: normalizeSpecialty(document.specialty || extractedData.specialty),
    service_date: toStringValue(extractedData.service_date) || document.document_date,
    invoice_date: toStringValue(extractedData.invoice_date) || document.document_date,
    total_amount: totalAmount,
    patient_paid_amount: patientPaidAmount,
    is_paid: toBoolean(extractedData.is_paid),
    payment_date: toStringValue(extractedData.payment_date),
    payment_method: toStringValue(extractedData.payment_method),
    secu_base: extractedData.secu_base == null ? null : toMoney(extractedData.secu_base, 0),
    secu_rate: extractedData.secu_rate == null ? null : toMoney(extractedData.secu_rate, 0),
    secu_reimbursed: secuReimbursed,
    mutuelle_reimbursed: mutuelleReimbursed,
    remaining_patient_charge: correctedRemainingCharge,
    document_url: document.file_url,
    notes: document.description,
    metadata: {
      ...extractedData,
      source_backend: 'document-fallback'
    },
    created_at: document.created_at,
    updated_at: document.updated_at
  }
}

function sortInvoices(invoices: MedicalInvoice[]) {
  return [...invoices].sort((left, right) => {
    if (left.service_date !== right.service_date) {
      return right.service_date.localeCompare(left.service_date)
    }
    return right.created_at.localeCompare(left.created_at)
  })
}

function normalizeLoadedInvoice(invoice: MedicalInvoice): MedicalInvoice {
  return {
    ...invoice,
    total_amount: toMoney(invoice.total_amount, 0),
    patient_paid_amount: toMoney(invoice.patient_paid_amount, 0),
    secu_reimbursed: invoice.secu_reimbursed == null ? null : toMoney(invoice.secu_reimbursed, 0),
    mutuelle_reimbursed: invoice.mutuelle_reimbursed == null ? null : toMoney(invoice.mutuelle_reimbursed, 0),
    remaining_patient_charge: buildRemainingCharge(invoice)
  }
}

function limitInvoices(invoices: MedicalInvoice[], limit: number | null | undefined) {
  if (typeof limit !== 'number' || !Number.isFinite(limit)) {
    return invoices
  }

  return invoices.slice(0, Math.max(0, Math.floor(limit)))
}

async function listFallbackInvoices(patientId: string): Promise<MedicalInvoice[]> {
  const documents = await getMedicalDocuments(patientId, FALLBACK_DOCUMENT_TYPE, null)

  return sortInvoices(
    documents
      .filter(isFallbackInvoiceDocument)
      .map(mapFallbackDocumentToInvoice)
  )
}

async function getFallbackInvoiceById(invoiceId: string): Promise<MedicalDocument> {
  const { data, error } = await supabase
    .from('medical_documents')
    .select('*')
    .eq('id', invoiceId)
    .maybeSingle()

  if (error) {
    throw new Error(`Erreur récupération facture de secours: ${error.message}`)
  }

  if (!data) {
    throw new Error('Facture introuvable')
  }

  return data as MedicalDocument
}

function hasOwnKey<T extends object>(value: T, key: keyof T) {
  return Object.prototype.hasOwnProperty.call(value, key)
}

function mergeFallbackInvoiceInput(
  currentInvoice: MedicalInvoice,
  updates: Partial<MedicalInvoiceInput>
): MedicalInvoiceInput {
  return {
    patient_id: currentInvoice.patient_id,
    invoice_number: hasOwnKey(updates, 'invoice_number') ? updates.invoice_number ?? null : currentInvoice.invoice_number,
    practitioner_name: hasOwnKey(updates, 'practitioner_name')
      ? toStringValue(updates.practitioner_name) || 'Praticien non identifié'
      : currentInvoice.practitioner_name,
    medical_facility: hasOwnKey(updates, 'medical_facility')
      ? updates.medical_facility ?? null
      : currentInvoice.medical_facility,
    specialty: hasOwnKey(updates, 'specialty')
      ? normalizeSpecialty(updates.specialty)
      : currentInvoice.specialty,
    service_date: hasOwnKey(updates, 'service_date')
      ? toStringValue(updates.service_date) || currentInvoice.service_date
      : currentInvoice.service_date,
    invoice_date: hasOwnKey(updates, 'invoice_date')
      ? toStringValue(updates.invoice_date) || currentInvoice.invoice_date
      : currentInvoice.invoice_date,
    total_amount: hasOwnKey(updates, 'total_amount')
      ? toMoney(updates.total_amount, currentInvoice.total_amount)
      : currentInvoice.total_amount,
    patient_paid_amount: hasOwnKey(updates, 'patient_paid_amount')
      ? toMoney(updates.patient_paid_amount, currentInvoice.patient_paid_amount || 0)
      : currentInvoice.patient_paid_amount || 0,
    is_paid: hasOwnKey(updates, 'is_paid')
      ? toBoolean(updates.is_paid, currentInvoice.is_paid)
      : currentInvoice.is_paid,
    payment_date: hasOwnKey(updates, 'payment_date')
      ? updates.payment_date ?? null
      : currentInvoice.payment_date,
    payment_method: hasOwnKey(updates, 'payment_method')
      ? updates.payment_method ?? null
      : currentInvoice.payment_method,
    secu_base: hasOwnKey(updates, 'secu_base')
      ? updates.secu_base == null ? null : toMoney(updates.secu_base, 0)
      : currentInvoice.secu_base,
    secu_rate: hasOwnKey(updates, 'secu_rate')
      ? updates.secu_rate == null ? null : toMoney(updates.secu_rate, 0)
      : currentInvoice.secu_rate,
    secu_reimbursed: hasOwnKey(updates, 'secu_reimbursed')
      ? updates.secu_reimbursed == null ? null : toMoney(updates.secu_reimbursed, 0)
      : currentInvoice.secu_reimbursed,
    mutuelle_reimbursed: hasOwnKey(updates, 'mutuelle_reimbursed')
      ? updates.mutuelle_reimbursed == null ? null : toMoney(updates.mutuelle_reimbursed, 0)
      : currentInvoice.mutuelle_reimbursed,
    document_url: hasOwnKey(updates, 'document_url')
      ? updates.document_url ?? null
      : currentInvoice.document_url,
    notes: hasOwnKey(updates, 'notes')
      ? updates.notes ?? null
      : currentInvoice.notes,
    metadata: {
      ...getSafeMetadata(currentInvoice.metadata),
      ...getSafeMetadata(updates.metadata)
    }
  }
}

async function createInvoiceInTable(input: MedicalInvoiceInput): Promise<MedicalInvoice> {
  const normalized = sanitizeInvoiceInput(input)
  const payload = {
    ...normalized,
    remaining_patient_charge: buildRemainingCharge(normalized)
  }

  const { data, error } = await supabase
    .from('medical_invoices')
    .insert(payload)
    .select()
    .single()

  if (error) {
    if (isMissingRelationError(error, ['public.medical_invoices', 'medical_invoices'])) {
      setInvoiceStorageMode('document-fallback')
      return createInvoiceInFallback(normalized)
    }
    throw new Error(`Erreur lors de la création de la facture: ${error.message}`)
  }

  setInvoiceStorageMode('table')
  return normalizeLoadedInvoice(data as MedicalInvoice)
}

async function createInvoiceInFallback(input: MedicalInvoiceInput): Promise<MedicalInvoice> {
  const document = await createMedicalDocument(buildFallbackDocument(input))
  return mapFallbackDocumentToInvoice(document)
}

function invoiceMatchesSearch(
  invoice: MedicalInvoice,
  searchTerm: string,
  filters?: {
    specialty?: MedicalSpecialty
    dateFrom?: string
    dateTo?: string
    isPaid?: boolean
    minAmount?: number
    maxAmount?: number
  }
) {
  const normalizedSearch = normalizeText(searchTerm)

  if (normalizedSearch) {
    const haystack = normalizeText([
      invoice.practitioner_name,
      invoice.medical_facility,
      invoice.invoice_number,
      invoice.notes
    ].filter(Boolean).join(' '))

    if (!haystack.includes(normalizedSearch)) {
      return false
    }
  }

  if (filters?.specialty && invoice.specialty !== filters.specialty) {
    return false
  }

  if (filters?.dateFrom && invoice.service_date < filters.dateFrom) {
    return false
  }

  if (filters?.dateTo && invoice.service_date > filters.dateTo) {
    return false
  }

  if (typeof filters?.isPaid === 'boolean' && invoice.is_paid !== filters.isPaid) {
    return false
  }

  if (typeof filters?.minAmount === 'number' && invoice.total_amount < filters.minAmount) {
    return false
  }

  if (typeof filters?.maxAmount === 'number' && invoice.total_amount > filters.maxAmount) {
    return false
  }

  return true
}

function summarizeLifetime(invoices: MedicalInvoice[]) {
  return invoices.reduce(
    (summary, invoice) => {
      const reimbursed = toMoney(invoice.secu_reimbursed, 0) + toMoney(invoice.mutuelle_reimbursed, 0)
      summary.total_amount += toMoney(invoice.total_amount, 0)
      summary.total_patient_charge += toMoney(invoice.remaining_patient_charge, buildRemainingCharge(invoice))
      summary.total_reimbursed += reimbursed
      summary.invoice_count += 1
      return summary
    },
    {
      total_amount: 0,
      total_patient_charge: 0,
      total_reimbursed: 0,
      invoice_count: 0
    }
  )
}

export async function getInvoiceStorageMode(forceRefresh = false): Promise<InvoiceStorageMode> {
  if (!forceRefresh && invoiceStorageModePromise) {
    return invoiceStorageModePromise
  }

  invoiceStorageModePromise = (async () => {
    const { error } = await supabase.from('medical_invoices').select('id', { count: 'exact', head: true })

    if (!error) {
      return 'table'
    }

    if (isMissingRelationError(error, ['public.medical_invoices', 'medical_invoices'])) {
      return 'document-fallback'
    }

    throw new Error(`Erreur vérification factures: ${error.message}`)
  })()

  return invoiceStorageModePromise
}

export async function createMedicalInvoice(invoice: MedicalInvoiceInput): Promise<MedicalInvoice> {
  const validation = validateInvoiceInput(invoice)
  if (!validation.isValid) {
    throw new Error(validation.errors.join(' '))
  }

  const mode = await getInvoiceStorageMode()
  return mode === 'table'
    ? createInvoiceInTable(invoice)
    : createInvoiceInFallback(invoice)
}

export async function getMedicalInvoices(
  patientId: string,
  specialty?: MedicalSpecialty,
  limit: number | null = DEFAULT_LIMIT
): Promise<MedicalInvoice[]> {
  const mode = await getInvoiceStorageMode()

  if (mode === 'document-fallback') {
    const invoices = await listFallbackInvoices(patientId)
    const filtered = specialty ? invoices.filter((invoice) => invoice.specialty === specialty) : invoices
    return limitInvoices(filtered, limit)
  }

  let query = supabase
    .from('medical_invoices')
    .select('*')
    .eq('patient_id', patientId)
    .order('service_date', { ascending: false })
    .order('created_at', { ascending: false })

  if (specialty) {
    query = query.eq('specialty', specialty)
  }

  if (typeof limit === 'number' && Number.isFinite(limit)) {
    query = query.limit(Math.max(0, Math.floor(limit)))
  }

  const { data, error } = await query

  if (error) {
    if (isMissingRelationError(error, ['public.medical_invoices', 'medical_invoices'])) {
      setInvoiceStorageMode('document-fallback')
      return getMedicalInvoices(patientId, specialty, limit)
    }
    throw new Error(`Erreur lors de la récupération des factures: ${error.message}`)
  }

  setInvoiceStorageMode('table')
  return (data || []).map((invoice) => normalizeLoadedInvoice(invoice as MedicalInvoice))
}

export async function updateMedicalInvoice(
  invoiceId: string,
  updates: Partial<MedicalInvoiceInput>
): Promise<MedicalInvoice> {
  const mode = await getInvoiceStorageMode()

  if (mode === 'document-fallback') {
    const currentDocument = await getFallbackInvoiceById(invoiceId)
    const currentInvoice = mapFallbackDocumentToInvoice(currentDocument)
    const mergedInvoiceInput = mergeFallbackInvoiceInput(currentInvoice, updates)
    const updatedDocument = await updateMedicalDocument(invoiceId, buildFallbackDocument(mergedInvoiceInput))
    return mapFallbackDocumentToInvoice(updatedDocument)
  }

  const { data: currentData, error: currentError } = await supabase
    .from('medical_invoices')
    .select('*')
    .eq('id', invoiceId)
    .single()

  if (currentError) {
    if (isMissingRelationError(currentError, ['public.medical_invoices', 'medical_invoices'])) {
      setInvoiceStorageMode('document-fallback')
      return updateMedicalInvoice(invoiceId, updates)
    }
    throw new Error(`Erreur lors de la récupération de la facture: ${currentError.message}`)
  }

  const currentInvoice = currentData as MedicalInvoice
  const mergedInvoice = mergeFallbackInvoiceInput(currentInvoice, updates)
  const payload = {
    ...updates,
    specialty: mergedInvoice.specialty,
    patient_paid_amount: mergedInvoice.patient_paid_amount,
    secu_reimbursed: mergedInvoice.secu_reimbursed,
    mutuelle_reimbursed: mergedInvoice.mutuelle_reimbursed,
    total_amount: mergedInvoice.total_amount,
    remaining_patient_charge: buildRemainingCharge(mergedInvoice)
  }

  const { data, error } = await supabase
    .from('medical_invoices')
    .update(payload)
    .eq('id', invoiceId)
    .select()
    .single()

  if (error) {
    if (isMissingRelationError(error, ['public.medical_invoices', 'medical_invoices'])) {
      setInvoiceStorageMode('document-fallback')
      return updateMedicalInvoice(invoiceId, updates)
    }
    throw new Error(`Erreur lors de la mise à jour de la facture: ${error.message}`)
  }

  setInvoiceStorageMode('table')
  return normalizeLoadedInvoice(data as MedicalInvoice)
}

export async function deleteMedicalInvoice(invoiceId: string): Promise<void> {
  const mode = await getInvoiceStorageMode()

  if (mode === 'document-fallback') {
    await deleteMedicalDocument(invoiceId)
    return
  }

  const { error } = await supabase
    .from('medical_invoices')
    .delete()
    .eq('id', invoiceId)

  if (error) {
    if (isMissingRelationError(error, ['public.medical_invoices', 'medical_invoices'])) {
      setInvoiceStorageMode('document-fallback')
      await deleteMedicalInvoice(invoiceId)
      return
    }
    throw new Error(`Erreur lors de la suppression de la facture: ${error.message}`)
  }
}

export async function searchMedicalInvoices(
  patientId: string,
  searchTerm: string,
  filters?: {
    specialty?: MedicalSpecialty
    dateFrom?: string
    dateTo?: string
    isPaid?: boolean
    minAmount?: number
    maxAmount?: number
  }
): Promise<MedicalInvoice[]> {
  const invoices = await getMedicalInvoices(patientId, undefined, null)
  return invoices.filter((invoice) => invoiceMatchesSearch(invoice, searchTerm, filters))
}

export async function getMonthlyExpenses(patientId: string, year?: number): Promise<MonthlyExpenses[]> {
  const invoices = await getMedicalInvoices(patientId, undefined, null)
  const filteredInvoices = typeof year === 'number'
    ? invoices.filter((invoice) => invoice.service_date.startsWith(`${year}-`))
    : invoices

  const byMonth = new Map<string, MonthlyExpenses>()

  filteredInvoices.forEach((invoice) => {
    const month = `${invoice.service_date.slice(0, 7)}-01`
    const current = byMonth.get(month) || {
      patient_id: patientId,
      month,
      invoice_count: 0,
      total_amount: 0,
      total_secu_reimbursed: 0,
      total_mutuelle_reimbursed: 0,
      total_patient_charge: 0
    }

    current.invoice_count += 1
    current.total_amount += toMoney(invoice.total_amount, 0)
    current.total_secu_reimbursed = toMoney(current.total_secu_reimbursed, 0) + toMoney(invoice.secu_reimbursed, 0)
    current.total_mutuelle_reimbursed = toMoney(current.total_mutuelle_reimbursed, 0) + toMoney(invoice.mutuelle_reimbursed, 0)
    current.total_patient_charge = toMoney(current.total_patient_charge, 0) +
      toMoney(invoice.remaining_patient_charge, buildRemainingCharge(invoice))

    byMonth.set(month, current)
  })

  return [...byMonth.values()].sort((left, right) => left.month.localeCompare(right.month))
}

export async function getExpensesBySpecialty(patientId: string): Promise<ExpensesBySpecialty[]> {
  const invoices = await getMedicalInvoices(patientId, undefined, null)
  const bySpecialty = new Map<MedicalSpecialty, ExpensesBySpecialty>()

  invoices.forEach((invoice) => {
    const specialty = normalizeSpecialty(invoice.specialty)
    const current = bySpecialty.get(specialty) || {
      patient_id: patientId,
      specialty,
      invoice_count: 0,
      total_amount: 0,
      total_patient_charge: 0,
      avg_patient_charge: 0
    }

    current.invoice_count += 1
    current.total_amount += toMoney(invoice.total_amount, 0)
    current.total_patient_charge = toMoney(current.total_patient_charge, 0) +
      toMoney(invoice.remaining_patient_charge, buildRemainingCharge(invoice))
    current.avg_patient_charge =
      current.invoice_count > 0
        ? Math.round((current.total_patient_charge / current.invoice_count) * 100) / 100
        : 0

    bySpecialty.set(specialty, current)
  })

  return [...bySpecialty.values()].sort(
    (left, right) => toMoney(right.total_patient_charge, 0) - toMoney(left.total_patient_charge, 0)
  )
}

export async function getTotalLifetimeExpenses(patientId: string): Promise<{
  total_amount: number
  total_patient_charge: number
  total_reimbursed: number
  invoice_count: number
}> {
  const invoices = await getMedicalInvoices(patientId, undefined, null)
  return summarizeLifetime(invoices)
}

export async function getUnpaidInvoices(patientId: string): Promise<MedicalInvoice[]> {
  const invoices = await getMedicalInvoices(patientId, undefined, null)
  return invoices.filter((invoice) => !invoice.is_paid)
}

export async function extractInvoiceDataFromPDF(file: File): Promise<Partial<MedicalInvoiceInput>> {
  const base: Partial<MedicalInvoiceInput> = {
    practitioner_name: file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim(),
    specialty: 'other',
    service_date: todayDate(),
    invoice_date: todayDate(),
    total_amount: 0
  }

  try {
    const legacyModule = await import('./invoice-extraction')
    const extracted = await legacyModule.extractInvoiceDataFromPDF(file)
    const metadata: Record<string, unknown> = {}

    if ((extracted as any).insurance_provider) {
      metadata.insurance_provider = (extracted as any).insurance_provider
    }

    if ((extracted as any).consultation_type) {
      metadata.consultation_type = (extracted as any).consultation_type
    }

    if ((extracted as any).doctor_name) {
      metadata.doctor_name = (extracted as any).doctor_name
    }

    return {
      practitioner_name: toStringValue((extracted as any).practitioner_name) ||
        toStringValue((extracted as any).provider_name) ||
        base.practitioner_name,
      medical_facility: toStringValue((extracted as any).medical_facility),
      specialty: normalizeSpecialty((extracted as any).specialty),
      service_date: toStringValue((extracted as any).service_date) || base.service_date,
      invoice_date:
        toStringValue((extracted as any).invoice_date) ||
        toStringValue((extracted as any).service_date) ||
        base.invoice_date,
      total_amount: toMoney((extracted as any).total_amount, 0),
      notes: toStringValue((extracted as any).notes) || toStringValue((extracted as any).details),
      metadata
    }
  } catch (error) {
    console.warn('Extraction facture dégradée:', error)
  }

  try {
    const text = await file.text()
    const amountMatch = text.match(/(\d+(?:[.,]\d{2})?)\s*€/)
    const dateMatch = text.match(/\b(\d{4}-\d{2}-\d{2}|\d{2}[\/.-]\d{2}[\/.-]\d{2,4})\b/)
    const practitionerMatch = text.match(/(?:Dr\.?|Docteur)\s+([A-ZÀ-ÿ][^\n,]+)/i)

    return {
      ...base,
      practitioner_name: practitionerMatch?.[1]?.trim() || base.practitioner_name,
      total_amount: amountMatch ? toMoney(amountMatch[1], 0) : 0,
      service_date: dateMatch?.[1]?.replace(/\./g, '-').replace(/\//g, '-') || base.service_date,
      invoice_date: dateMatch?.[1]?.replace(/\./g, '-').replace(/\//g, '-') || base.invoice_date
    }
  } catch {
    return base
  }
}

export function getSpecialtyLabel(specialty: MedicalSpecialty): string {
  const labels: Record<MedicalSpecialty, string> = {
    general_medicine: 'Médecine générale',
    gastroenterology: 'Gastroentérologie',
    gynecology: 'Gynécologie',
    laboratory: 'Laboratoire',
    radiology: 'Radiologie',
    pharmacy: 'Pharmacie',
    physiotherapy: 'Kinésithérapie',
    psychology: 'Psychologie',
    dermatology: 'Dermatologie',
    cardiology: 'Cardiologie',
    endocrinology: 'Endocrinologie',
    rheumatology: 'Rhumatologie',
    neurology: 'Neurologie',
    surgery: 'Chirurgie',
    emergency: 'Urgences',
    other: 'Autre'
  }

  return labels[specialty] || specialty
}

export function getSpecialtyColor(specialty: MedicalSpecialty): string {
  const colors: Record<MedicalSpecialty, string> = {
    general_medicine: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    gastroenterology: 'text-green-400 bg-green-400/10 border-green-400/20',
    gynecology: 'text-pink-400 bg-pink-400/10 border-pink-400/20',
    laboratory: 'text-red-400 bg-red-400/10 border-red-400/20',
    radiology: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
    pharmacy: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    physiotherapy: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
    psychology: 'text-indigo-400 bg-indigo-400/10 border-indigo-400/20',
    dermatology: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
    cardiology: 'text-rose-400 bg-rose-400/10 border-rose-400/20',
    endocrinology: 'text-teal-400 bg-teal-400/10 border-teal-400/20',
    rheumatology: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
    neurology: 'text-violet-400 bg-violet-400/10 border-violet-400/20',
    surgery: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
    emergency: 'text-red-500 bg-red-500/10 border-red-500/20',
    other: 'text-gray-400 bg-gray-400/10 border-gray-400/20'
  }

  return colors[specialty] || colors.other
}

export function getSpecialtyIcon(specialty: MedicalSpecialty): string {
  const icons: Record<MedicalSpecialty, string> = {
    general_medicine: '👨‍⚕️',
    gastroenterology: '🫃',
    gynecology: '👩‍⚕️',
    laboratory: '🔬',
    radiology: '📷',
    pharmacy: '💊',
    physiotherapy: '🏃',
    psychology: '🧠',
    dermatology: '🫳',
    cardiology: '❤️',
    endocrinology: '🧪',
    rheumatology: '🦴',
    neurology: '🧠',
    surgery: '🏥',
    emergency: '🚨',
    other: '📄'
  }

  return icons[specialty] || icons.other
}

export function formatCurrency(amount: number): string {
  const safeAmount = Number.isFinite(amount) ? amount : 0
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(safeAmount)
}

export function calculateReimbursementRate(totalAmount: number, reimbursedAmount: number): number {
  if (!totalAmount || totalAmount <= 0) return 0
  return Math.round((Math.max(reimbursedAmount, 0) / totalAmount) * 100)
}

export function getPaymentStatusColor(isPaid: boolean): string {
  return isPaid
    ? 'text-green-400 bg-green-400/10 border-green-400/20'
    : 'text-orange-400 bg-orange-400/10 border-orange-400/20'
}

export function getPaymentStatusLabel(isPaid: boolean): string {
  return isPaid ? 'Payée' : 'En attente'
}

export function validateInvoiceInput(input: MedicalInvoiceInput): { isValid: boolean; errors: string[] } {
  const errors: string[] = []
  const practitioner = toStringValue(input.practitioner_name)
  const serviceDate = toStringValue(input.service_date)
  const invoiceDate = toStringValue(input.invoice_date)
  const totalAmount = toMoney(input.total_amount, 0)

  if (!practitioner) {
    errors.push('Le nom du praticien est obligatoire')
  }

  if (!serviceDate) {
    errors.push('La date de service est obligatoire')
  }

  if (!invoiceDate) {
    errors.push('La date de facture est obligatoire')
  }

  if (totalAmount <= 0) {
    errors.push('Le montant total doit être supérieur à 0')
  }

  if (serviceDate && invoiceDate && new Date(serviceDate) > new Date(invoiceDate)) {
    errors.push('La date de service ne peut pas être postérieure à la date de facture')
  }

  if (serviceDate && new Date(serviceDate) > new Date()) {
    errors.push('La date de service ne peut pas être dans le futur')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}
